import { useEffect, useState } from 'react';

/** The preference, as the platform states it. */
const REDUCE_QUERY = '(prefers-reduced-motion: reduce)';

/**
 * Whether this reader has asked for less motion (spec 075 FR-004).
 *
 * The stylesheet answers this question for anything expressed as a CSS
 * transition or animation — see the `prefers-reduced-motion` rule in
 * `globals.css`. This is the same question for motion that is *not* a style
 * rule: view transitions today, and whatever choreography follows.
 *
 * One hook rather than a `matchMedia` call at each site, so the two answers
 * cannot drift. It is not a seam and takes no configuration: there is one
 * implementation and several consumers, which is a shared utility rather than
 * the abstraction rule 2.4 forbids.
 *
 * Live, not sampled. A reader who changes the preference while the application
 * is open is honoured on the next interaction rather than at the next reload
 * (FR-005) — which is the whole difference between respecting a preference and
 * having read it once.
 *
 * The shape deliberately matches `ShellFrame`'s viewport listener: read once,
 * subscribe, clean up.
 */
export function useReducedMotion(): boolean {
  // jsdom implements no `matchMedia`, and the test setup stubs it — but a
  // consumer rendered before that stub would still see `undefined`, so the
  // initial read is guarded and defaults to "wants motion".
  const [reduced, setReduced] = useState(
    () => window.matchMedia?.(REDUCE_QUERY).matches ?? false,
  );

  useEffect(() => {
    const query = window.matchMedia(REDUCE_QUERY);
    const sync = (event: MediaQueryList | MediaQueryListEvent) => {
      setReduced(event.matches);
    };
    sync(query);
    query.addEventListener('change', sync);
    return () => {
      query.removeEventListener('change', sync);
    };
  }, []);

  return reduced;
}
